<template>
		<header>
			<h1>Группы характеристик</h1>
			<div class="actions">
				<VButton
					theme="link"
					@click="openCard()"
				>
					<template #icon>
						<VIcon material="add" />
					</template>
					Добавить группу характеристик
				</VButton>
			</div>
		</header>

		<div
			ref="dropAreaRef"
			class="group-list draggable"
		>
			<article
				v-for="item in items"
				:key="item.id ?? 'none'"
				class="group-list-item"
				:class="{
            selected: (selected?.id ?? null) === item.id,
            draggable: item.id !== null,
        }"
				:draggable="item.id !== null"
				:data-drag-identity="item.id === null ? -1 : item.id"
				@click="changeSelected(item)"
			>
				<span
					:title="item.name"
				>
					{{ item.name }}
				</span>

				<VButton
					v-if="item.id !== null"
					theme="link"
					@click="openCard(item, $event)"
				>
					<template #icon>
						<VIcon material="edit" />
					</template>
				</VButton>
			</article>
		</div>
</template>

<script lang="ts">

import CharacteristicGroup         from '@/domains/CharacteristicGroups/CharacteristicGroup';
import CharacteristicGroupsService from '@/domains/CharacteristicGroups/CharacteristicGroupsService';

/**
 * Элемент списка для рендера: реальная группа или фейковая "Без группы".
 * "Без группы" — это UI-представление выбора null наружу.
 */
type GroupItem = { id: number | null; name: string };

export default defineComponent({
	name: 'CharacteristicGroupsRegistry',

	props: {
		modelValue: {
			type:    Object as () => CharacteristicGroup | null,
			default: null,
		},
	},

	emits: ['update:modelValue'],

	data() {
		return {
			items:        [{ id: null, name: 'Без группы' }] as GroupItem[],
			selected:     this.modelValue,
			dragImageEl:  null as HTMLElement | null,
		};
	},

	watch: {
		modelValue(value: CharacteristicGroup | null) {
			this.selected = value;
		},
	},

	async mounted() {
		await this.refresh();
		this.setupDragAndDrop();
	},

	beforeUnmount() {
		this.teardownDragAndDrop();
	},

	methods: {
		async refresh(): Promise<void> {
			const response = await CharacteristicGroupsService.getList();

			/** не отображаем {"name":null,"id":13,"version":2,"isDeleted":true}  */
			const activeGroups = response.filter(group => !group.isDeleted && group.name !== null);

			this.items = [
				{ id: null, name: 'Без группы' },
				...activeGroups,
			];

			// Если выбранная группа исчезла — сбрасываем на "Без группы"
			const selectedId = this.selected?.id ?? null;
			const stillExists = selectedId === null
				|| response.some(group => group.id === selectedId);

			if (!stillExists) {
				this.selected = null;
				this.$emit('update:modelValue', null);
			}
		},

		changeSelected(item: GroupItem) {
			// "Без группы" (item.id === null) → эмитим null наружу.
			// Реальная группа → эмитим её объект, который лежит в items
			// после refresh — это уже полноценный CharacteristicGroup.
			if (item.id === null) {
				this.selected = null;
				this.$emit('update:modelValue', null);
			} else {
				this.selected = item as CharacteristicGroup;
				this.$emit('update:modelValue', item as CharacteristicGroup);
			}
		},

		async openCard(entity: CharacteristicGroup | null = null, event?: MouseEvent) {
			event?.stopPropagation();

			await this.$core.dialogs.CharacteristicGroupCard.showAsPromise(entity ? { entity } : {});
      this.refresh();
		},

		// ==================== Drag and drop ====================

		setupDragAndDrop() {
			const dropArea = this.$refs.dropAreaRef as HTMLElement | undefined;
			if (!dropArea) return;

			dropArea.addEventListener('dragstart', this.onDragStart);
			dropArea.addEventListener('dragenter', this.onDragEnter);
			dropArea.addEventListener('dragleave', this.onDragLeave);
			dropArea.addEventListener('dragover',  this.onDragOver);
			dropArea.addEventListener('drop',      this.onDrop);
		},

		teardownDragAndDrop() {
			const dropArea = this.$refs.dropAreaRef as HTMLElement | undefined;
			if (!dropArea) return;

			dropArea.removeEventListener('dragstart', this.onDragStart);
			dropArea.removeEventListener('dragenter', this.onDragEnter);
			dropArea.removeEventListener('dragleave', this.onDragLeave);
			dropArea.removeEventListener('dragover',  this.onDragOver);
			dropArea.removeEventListener('drop',      this.onDrop);
		},

		onDragStart(event: DragEvent) {
			const dropArea = this.$refs.dropAreaRef as HTMLElement;
			const draggingEl = event.target as HTMLElement;

			dropArea.classList.add('dragging-in-progress');

			if (draggingEl.hasAttribute('data-drag-identity') && event.dataTransfer) {
				event.dataTransfer.setData('text/plain', draggingEl.getAttribute('data-drag-identity') ?? '');
				event.dataTransfer.effectAllowed = 'move';

				this.dragImageEl = this.createDragImageEl();
				event.dataTransfer.setDragImage(this.dragImageEl, -5, -5);
			}
		},

		createDragImageEl(): HTMLElement {
			const dragImage = document.createElement('div');
			dragImage.classList.add('mds-drag-image', 'empty');
			dragImage.style.position      = 'fixed';
			dragImage.style.bottom        = '-1000px';
			dragImage.style.pointerEvents = 'none';
			document.body.append(dragImage);
			return dragImage;
		},

		onDragEnter(event: DragEvent) {
			const targetEl = (event.target as HTMLElement).closest('article');
			targetEl?.classList.add('drag-hovered-el');
		},

		onDragLeave(event: DragEvent) {
			const targetEl = (event.target as HTMLElement).closest('article');
			targetEl?.classList.remove('drag-hovered-el');
		},

		onDragOver(event: DragEvent) {
			event.preventDefault();
			const targetEl = (event.target as HTMLElement).closest('article');
			if (!targetEl || !targetEl.classList.contains('drag-hovered-el')) return;

			const rect = targetEl.getBoundingClientRect();
			const verticalMark = this.calculateVerticalMark(rect.y, rect.height);

			if (event.clientY > verticalMark) {
				targetEl.classList.add('paste-after');
			} else {
				targetEl.classList.remove('paste-after');
			}
		},

		async onDrop(event: DragEvent) {
			event.preventDefault();
			event.stopPropagation();

			if (!event.dataTransfer) return;

			const movedElementId = parseInt(event.dataTransfer.getData('text'), 10);

			const targetEl = (event.target as HTMLElement).closest('article');
			if (!targetEl) return;

			const targetElementId = parseInt(targetEl.getAttribute('data-drag-identity') ?? '', 10);
			if (targetElementId <= 0) return;

			const dropArea = this.$refs.dropAreaRef as HTMLElement;
			dropArea.classList.remove('dragging-in-progress');

			dropArea.querySelectorAll('.drag-hovered-el').forEach(el => {
				el.classList.remove('drag-hovered-el');
			});

			const rect = targetEl.getBoundingClientRect();
			const verticalMark = this.calculateVerticalMark(rect.y, rect.height);
			const insertAfter = event.clientY > verticalMark;

			if (this.dragImageEl) {
				document.body.removeChild(this.dragImageEl);
				this.dragImageEl = null;
			}

			const success = await CharacteristicGroupsService.changeSort(
				movedElementId,
				targetElementId,
				insertAfter,
			);
			if (success) {
				await this.refresh();
			}
		},

		calculateVerticalMark(yPosition: number, elementHeight: number): number {
			return Math.floor(yPosition + elementHeight * 0.4);
		},
	},
});
</script>
